import React, { useMemo } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";

import jwtDecode from "jwt-decode";
import { connect } from "react-redux";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { Header } from "../components";
import Dashboard from "../pages/Dashboard";
import RaiseContactEvent from "../pages/RaiseContactEvent";
import Resources from "../pages/Resources";
import Support from "../pages/Support";
import Privacy from "../pages/Privacy";
import TermsAndConditions from "../pages/TermsAndConditions";
import AuditHistory from "../pages/audit/AuditHistory";
import CamListForAuditHistory from "../pages/audit/CamListForAuditHistory";
import CamList from "../pages/audit/CamList";
import CamResponse from "../pages/audit/CamResponse";
import Login from "../pages/auth/Login";
import LoginFailed from "../pages/auth/LoginFailed";
import OauthHandler from "../pages/auth/OauthHandler";
import MyDetails from "../pages/details/MyDetails";
import MyLegalEntities from "../pages/details/MyLegalEntities";
import MyLegalEntity from "../pages/details/MyLegalEntity";
import MyPremises from "../pages/details/MyPremises";
import MyPremise from "../pages/details/MyPremise";
import AuditStatusReport from "../pages/dhb/AuditStatusReport";
import CamStatusReport from "../pages/dhb/CamStatusReport";
import VerifyRI from "../pages/registers/VerifyRI";
import SearchRestrictionNotice from "../pages/registers/SearchRestrictionNotice";
import AmendRI from "../pages/regulatory-instruments/AmendRI";
import ApplyRI from "../pages/regulatory-instruments/ApplyRI";
import RenewRI from "../pages/regulatory-instruments/RenewRI";
import ControlledDrugsReporting from "../pages/regulatory-instruments/ControlledDrugsReporting";
import RegulatoryInstruments from "../pages/regulatory-instruments/RegulatoryInstruments";
import ImportExportNotification from "../pages/regulatory-instruments/ImportExportNotification";
import ActiveRiList from "../pages/regulatory-instruments/ActiveRiList";
import Loading from "../pages/util/Loading";
import routes from "../constants/routes";
import { User, Role } from "../constants/interfaces";
import { authActions } from "../reducers/authentication";
import hasUserRole from "../utils/hasUserRole";
import { userActions } from "../reducers/user";
import { fromEvent, merge, of, Subject, timer, race } from "rxjs";
import {
  take,
  tap,
  switchMap,
  repeatWhen,
  debounceTime,
  mergeMap,
  map,
  catchError
} from "rxjs/operators";

type RouteProps = {
  path: string;
  exact?: boolean;
  component?: any;
  render?: any;
};

const authedRoutes = (user: User): RouteProps[] => {
  // General routes
  const permissableRoutes: RouteProps[] = [
    {
      path: routes.CONTACT.url,
      exact: true,
      component: RaiseContactEvent
    },
    {
      path: routes.RESOURCES.url,
      exact: true,
      component: Resources
    },
    {
      path: routes.SUPPORT.url,
      exact: true,
      render: () => <Support authenticated={true} />
    },
    {
      path: routes.TERMS.url,
      exact: true,
      render: () => <TermsAndConditions authenticated={true} />
    },
    {
      path: routes.PRIVACY.url,
      exact: true,
      render: () => <Privacy authenticated={true} />
    }
  ];
  // Detail routes
  permissableRoutes.push({ path: routes.MY_DETAILS.url, component: MyDetails });
  permissableRoutes.push(
    { path: `${routes.MY_LEGAL_ENTITY.url}:id`, component: MyLegalEntity },
    { path: routes.MY_LEGAL_ENTITIES.url, component: MyLegalEntities }
  );
  permissableRoutes.push(
    { path: `${routes.MY_PREMISE.url}:id`, component: MyPremise },
    { path: routes.MY_PREMISES.url, component: MyPremises }
  );
  // Regulatory instrument routes
  permissableRoutes.push(
    {
      path: routes.APPLY_RI.url,
      component: ApplyRI
    },
    {
      path: `${routes.AMEND_RI.url}:id`,
      component: AmendRI
    },
    {
      path: `${routes.RENEW_RI.url}:id`,
      component: RenewRI
    },
    {
      path: routes.CONTROLLED_DRUGS.url,
      component: ControlledDrugsReporting
    },
    {
      path: routes.REGULATORY_INSTRUMENTS.url,
      component: RegulatoryInstruments
    },
    {
      path: routes.IMPORT_EXPORT_LIST.url,
      component: ActiveRiList
    },
    {
      path: `${routes.IMPORT_EXPORT_NOTIFICATION.url}:id`,
      component: ImportExportNotification
    }
  );
  // Audit routes
  permissableRoutes.push({
    path: routes.AUDIT_HISTORY.url,
    exact: true,
    component: AuditHistory
  });
  permissableRoutes.push({
    path: `${routes.CAM_LIST.url}:id`,
    exact: true,
    component: CamListForAuditHistory
  });
  hasUserRole(user, Role.MCOLS) &&
    permissableRoutes.push(
      {
        path: routes.CAM_LIST.url,
        exact: true,
        component: CamList
      },
      {
        path: `${routes.CAM_RESPONSE.url}:id`,
        component: CamResponse
      }
    );
  // Registers routes
  hasUserRole(user, Role.RESTRICTION_NOTICE) &&
    permissableRoutes.push({
      path: routes.SEARCH_RESTRICTION_NOTICE.url,
      component: SearchRestrictionNotice
    });
  hasUserRole(user, Role.VERIFY_RI) &&
    permissableRoutes.push({ path: routes.VERIFY_RI.url, component: VerifyRI });

  // DHB routes
  hasUserRole(user, Role.DHB) &&
    permissableRoutes.push(
      {
        path: routes.AUDIT_STATUS_REPORT.url,
        component: AuditStatusReport
      },
      {
        path: `${routes.CAM_STATUS_REPORT.url}:id`,
        component: CamStatusReport
      }
    );

  // Add Dashboard last as it acts as a fallback
  permissableRoutes.push({
    path: routes.HOME.url,
    component: Dashboard
  });
  return permissableRoutes;
};

const AuthRoutes = ({ user }: any) => (
  <div>
    <Header />
    <Switch>
      {authedRoutes(user).map(route => (
        <Route key={route.path} {...route} />
      ))}
    </Switch>
  </div>
);

const UnauthRoutes = () => (
  <Switch>
    <Route
      exact
      path={routes.SUPPORT.url}
      component={() => <Support authenticated={false} />}
    />
    <Route
      exact
      path={routes.TERMS.url}
      component={() => <TermsAndConditions authenticated={false} />}
    />
    <Route path={routes.OAUTH_ERROR.url} component={LoginFailed} />
    <Route path={routes.OAUTH_SUCCESS.url} component={OauthHandler} />
    <Route component={Login} />
  </Switch>
);

type Props = {
  classes?: any;
  loading: boolean;
  token: string | null;
  user: User | null;
  getUserDetails: () => Promise<void>;
  logoutUser: () => Promise<void>;
  getUserPrincipal: () => Promise<void>;
  setToken: (token: string) => void;
  sessionTimeout?: number;
  timeoutResponse?: number;
};

export const Router: React.FC<Props> = ({
  loading,
  user,
  token,
  getUserPrincipal,
  getUserDetails,
  setToken,
  logoutUser,
  sessionTimeout = 1500000, // 1000 * 60 * 25 (25mins)
  timeoutResponse = 300000 // 1000 * 60 * 5 (5mins)
}) => {
  const [inactivateBox, showInactivateBox] = React.useState(false);

  const clickActiveSubject$ = useMemo(() => {
    return new Subject();
  }, []);

  React.useEffect(() => {
    if (token) {
      setToken(token);

      const tokenTimeout$ = of(token).pipe(
        map(token => {
          const { exp } = jwtDecode(token);
          return exp * 1000 - Date.now();
        }),
        mergeMap(expirationTime => timer(Math.max(expirationTime, 0))),
        tap(() => {
          logoutUser();
        }),
        catchError((err, caught) => {
          logoutUser();
          return caught;
        })
      );

      const mousemove$ = fromEvent(document, "mousemove");
      const keypress$ = fromEvent(document, "keypress");

      const showBox$ = merge(keypress$, mousemove$, of("start")).pipe(
        debounceTime(sessionTimeout),
        take(1),
        tap(() => {
          showInactivateBox(true);
        }),
        repeatWhen(() => clickActiveSubject$),
        switchMap(() =>
          race(
            timer(timeoutResponse).pipe(
              tap(() => {
                logoutUser();
              })
            ),
            clickActiveSubject$
          )
        )
      );

      const subscription = showBox$.subscribe();
      const tokenSubscription = tokenTimeout$.subscribe();
      getUserPrincipal().then(getUserDetails);

      return () => {
        subscription.unsubscribe();
        tokenSubscription.unsubscribe();
      };
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <BrowserRouter basename="/mcols">
        {loading ? (
          <Loading />
        ) : user ? (
          <AuthRoutes user={user} />
        ) : (
          <UnauthRoutes />
        )}
      </BrowserRouter>
      <Dialog open={inactivateBox} fullWidth maxWidth="xs">
        <DialogTitle>Session Timeout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are being timed out due to inactivity. Please click "Active" to
            stay signed in
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            id="router-activeBox"
            onClick={() => {
              clickActiveSubject$.next("click");
              showInactivateBox(false);
            }}
            color="primary"
          >
            Active
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const mapDispatchToProps = {
  getUserPrincipal: authActions.getUserPrincipal,
  getUserDetails: userActions.getUserDetails,
  setToken: authActions.setToken,
  logoutUser: authActions.logoutUser
};

const mapStateToProps = ({ authentication, user }: any) => {
  return {
    error: authentication.error,
    loading: authentication.loading,
    token: authentication.token,
    user: user.user
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Router);
